import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useCourseStore } from '@/store/courseStore';
import { CourseGrid } from '@/components/course';
import { BackToTop } from '@/components/common';
import ErrorBoundary from '@/components/ErrorBoundary';
import SEOHead from '@/components/common/SEOHead';
import { CourseApi } from '@/services/courseApi';
import type { Audience, PublicCourse } from '@/types/course';
import {
  MENTOR_PHONE,
  MENTOR_ZALO_URL,
} from '@/components/student/course-detail/types';

/** Fallback order if audiences API fails: BA mới → kỹ thuật → AI → chứng chỉ */
const FALLBACK_AUDIENCE_ORDER = ['beginner', 'tech', 'ai', 'cert'] as const;

function getPrimaryAudienceCode(course: PublicCourse): string | null {
  const primary = course.audiences?.find((a) => a.isPrimary) || course.audiences?.[0];
  return primary?.code ?? null;
}

/** Flat list sorted by audience path order (LTR, top → bottom). No visual groups. */
function sortCoursesByPath(
  courses: PublicCourse[],
  audiences: Audience[]
): PublicCourse[] {
  const orderedCodes =
    audiences.length > 0
      ? [...audiences].sort((a, b) => a.sortOrder - b.sortOrder).map((a) => a.code)
      : [...FALLBACK_AUDIENCE_ORDER];

  const used = new Set<number>();
  const sorted: PublicCourse[] = [];

  for (const code of orderedCodes) {
    for (const course of courses) {
      if (used.has(course.id)) continue;
      if (getPrimaryAudienceCode(course) === code) {
        sorted.push(course);
        used.add(course.id);
      }
    }
  }

  for (const course of courses) {
    if (!used.has(course.id)) sorted.push(course);
  }

  return sorted;
}

const CoursesPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = (searchParams.get('search') || '').trim();
  const {
    courses,
    loading,
    error,
    fetchCourses,
    clearError,
  } = useCourseStore();

  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [zaloQr, setZaloQr] = useState('');

  useEffect(() => {
    fetchCourses({ limit: 50, page: 1 });
  }, [fetchCourses]);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(MENTOR_ZALO_URL, {
      width: 160,
      margin: 2,
      color: { dark: '#000000', light: '#FFFFFF' },
    })
      .then((url) => {
        if (!cancelled) setZaloQr(url);
      })
      .catch(() => {
        if (!cancelled) setZaloQr('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await CourseApi.getAudiences();
        if (!cancelled) setAudiences(items);
      } catch {
        if (!cancelled) setAudiences([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedCourses = useMemo(() => {
    const ordered = sortCoursesByPath(courses, audiences);
    if (!searchQuery) return ordered;
    const needle = searchQuery.toLowerCase();
    return ordered.filter((course) => {
      const haystack = `${course.name} ${course.code} ${course.shortDescription || ''}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [courses, audiences, searchQuery]);

  const handleCourseClick = (courseId: number) => {
    navigate(`/courses/${courseId}`);
  };

  const retry = () => {
    clearError();
    fetchCourses({ limit: 50, page: 1 });
  };

  return (
    <ErrorBoundary>
      <SEOHead
        title="Khóa học Business Analyst (BA) | Danh sách khóa học M2MBA"
        description="Khám phá danh sách các khóa học Business Analyst (BA) từ cơ bản đến nâng cao tại M2MBA. Học BA thực chiến, kèm cặp 1-1, dự án thực tế cùng chuyên gia."
        image="https://storage.googleapis.com/m2mba/f4872d86-57b4-4bb8-9492-f14a60f22280.jpg"
        noindex={false}
        keywords="khóa học business analyst, khóa học BA, đào tạo BA, BA cho người bắt đầu,AI,Automation Workflow, n8n,bmad method, API, Database, SQL, tích hợp hệ thống"
        tags={['Business Analyst', 'Khóa học BA', 'Đào tạo BA', 'AI', 'Automation', 'n8n', 'API', 'Database', 'SQL', 'tích hợp hệ thống']}
        url="/courses"
        type="website"
      />

      {sortedCourses.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Danh sách khóa học tại M2MBA',
            description:
              'Tổng hợp các khóa học Business Analyst (BA) từ nền tảng đến nâng cao, học trực tuyến và offline, được giảng dạy bởi chuyên gia thực chiến tại M2MBA.',
            itemListElement: sortedCourses.map((course, index) => ({
              '@type': 'Course',
              position: index + 1,
              name: course.name,
              description: course.metadescription || course.shortDescription,
              url: `https://m2mba.com/courses/${course.slug ?? course.id}`,
              provider: {
                '@type': 'Organization',
                name: 'M2MBA',
                url: 'https://m2mba.com',
              },
              inLanguage: 'vi',
              datePublished: new Date(course.createdAt).toISOString(),
              image:
                course.imageUrl ||
                'https://storage.googleapis.com/m2mba/f4872d86-57b4-4bb8-9492-f14a60f22280.jpg',
            })),
          })}
        </script>
      )}

      <div className="min-h-screen bg-m2-surface-bright pb-section pt-8 text-m2-on-surface md:pt-12">
        <div className="eh-container">
          <header className="mb-10 border-b border-m2-primary/20 pb-8 md:mb-12 md:pb-10">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
              <div className="max-w-2xl">
                <p className="eh-eyebrow text-m2-primary">M2MBA Academy</p>
                <h1 className="eh-display text-m2-primary">
                  Lộ trình kỹ năng cho BA hiện đại
                </h1>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-m2-on-surface-variant md:text-lg">
                  Tìm thấy mảnh ghép còn thiếu trong sự nghiệp của bạn với các khóa học thực chiến,
                  cập nhật xu hướng công nghệ mới nhất cho Business Analyst.
                </p>
              </div>
              <div className="hidden shrink-0 border border-m2-primary bg-m2-inverse-surface px-5 py-4 text-m2-inverse-on-surface md:block">
                <div className="text-sm font-semibold">
                  Certificate of Excellence
                </div>
                <div className="mt-1 text-xs text-m2-inverse-on-surface/80">
                  Đào tạo thực chiến được công nhận
                </div>
              </div>
            </div>
          </header>

          {!loading && !error && sortedCourses.length > 0 && (
            <p className="mb-6 text-sm font-semibold tracking-wide text-m2-on-surface-variant">
              {searchQuery
                ? `Kết quả cho “${searchQuery}” · ${sortedCourses.length} khóa`
                : `Tất cả khóa học · ${sortedCourses.length} khóa`}
            </p>
          )}

          <CourseGrid
            courses={sortedCourses}
            onCourseClick={handleCourseClick}
            loading={loading}
            error={error}
            onRetry={retry}
          />

          <section className="mt-section-sm border-t border-m2-primary/20 pt-10 md:pt-14">
            <div className="flex flex-col items-start justify-between gap-8 bg-m2-inverse-surface px-5 py-8 text-m2-inverse-on-surface sm:px-7 md:flex-row md:items-center md:px-10 md:py-10">
              <div className="max-w-xl">
                <h2 className="eh-title-sm text-m2-inverse-on-surface">
                  Bạn cần tư vấn lộ trình riêng?
                </h2>
                <p className="mt-3 text-base text-m2-inverse-on-surface/85">
                  Chat Zalo với đội ngũ Academy để được hỗ trợ chọn khóa học phù hợp nhất.
                  Hoặc bắt đầu từ{' '}
                  <Link to="/#stage" className="font-semibold text-m2-inverse-on-surface underline underline-offset-2">
                    lộ trình nghề nghiệp
                  </Link>
                  .
                </p>
                <p className="mt-3 text-lg font-bold tracking-wide text-m2-inverse-on-surface">
                  {MENTOR_PHONE}
                </p>
              </div>
              <div className="flex w-full flex-col items-center gap-4 sm:flex-row sm:items-center md:w-auto">
                <div className="shrink-0 rounded-eh border border-m2-inverse-on-surface/30 bg-white p-2">
                  {zaloQr ? (
                    <img
                      src={zaloQr}
                      alt={`QR chat Zalo ${MENTOR_PHONE}`}
                      className="h-32 w-32"
                    />
                  ) : (
                    <div className="flex h-32 w-32 items-center justify-center text-xs text-m2-on-surface-variant">
                      Đang tạo QR...
                    </div>
                  )}
                </div>
                <div className="flex w-full flex-col gap-2 sm:w-auto">
                  <a
                    href={MENTOR_ZALO_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-eh border border-m2-inverse-on-surface bg-m2-inverse-on-surface px-8 py-3 font-semibold text-m2-inverse-surface transition-colors hover:bg-m2-primary-fixed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-m2-inverse-on-surface"
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5" />
                    Chat Zalo
                  </a>
                  <a
                    href={MENTOR_ZALO_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="text-center text-sm text-m2-inverse-on-surface/80 underline underline-offset-2 hover:text-m2-inverse-on-surface"
                  >
                    zalo.me/{MENTOR_PHONE}
                  </a>
                </div>
              </div>
            </div>
          </section>

          <BackToTop />
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default CoursesPage;
